import { FC, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { type AppTheme } from '../../../theme/theme'

const sharedStyles = (theme: AppTheme) => ({
  font: 'inherit',
  padding: `${theme.spacing.xs + 2}px ${theme.spacing.xs}px`,
  border: '1px solid transparent',
  borderRadius: 0,
  background: 'none',
  boxSizing: 'border-box' as const,
  lineHeight: 'normal' as const,
})

const Input = styled('input')(({ theme }) => ({
  ...sharedStyles(theme),
  color: theme.colors.text.primary,
  textAlign: 'center',
  width: 'min-content',
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.colors.primary.main,
  },
  '&:focus': {
    borderColor: theme.colors.primary.main,
  },
}))

const SetDateButton = styled('button')(({ theme }) => ({
  ...sharedStyles(theme),
  color: theme.colors.primary.main,
  cursor: 'pointer',
  textDecoration: 'none',
  textAlign: 'center',
  '&:hover': {
    textDecoration: 'underline',
  },
}))

function toInputDate(apiDate: string): string {
  const [day, month, year] = apiDate.split('/')
  return `${year}-${month}-${day}`
}

function toApiDate(inputDate: string): string {
  const [year, month, day] = inputDate.split('-')
  return `${day}/${month}/${year}`
}

interface PreviewDateInputProps {
  value?: string
  onChange: (value: string | undefined) => void
}

export const PreviewDateInput: FC<PreviewDateInputProps> = ({ value, onChange }) => {
  const { t } = useTranslation()
  const [showInput, setShowInput] = useState(false)

  const min = useMemo(() => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }, [])

  if (!value && !showInput) {
    return (
      <SetDateButton
        type="button"
        onClick={() => {
          setShowInput(true)
          onChange(toApiDate(min))
        }}
      >
        {t('Set date')}
      </SetDateButton>
    )
  }

  return (
    <Input
      type="date"
      value={value ? toInputDate(value) : min}
      min={min}
      autoFocus={showInput}
      onChange={(e) => {
        onChange(e.target.value ? toApiDate(e.target.value) : undefined)
      }}
      onBlur={() => {
        if (!value) setShowInput(false)
      }}
    />
  )
}
